import { createDraftSafeSelector as createSelector } from '@reduxjs/toolkit';

import { Inject } from '@vk-hr-tek/core/ioc';

import { AbsencesListMapper, AbsencesMapper } from '../mappers';

import { AbsencesWithRootState as RootState } from './absences.state';

export const selectEmployeeAbsencesStatus = (state: RootState) =>
  state.absences.absenceList.status;

export const selectEmployeeAbsenceList = createSelector(
  [
    (state: RootState) => state.absences.absenceList.items,
    (_: RootState, inject: Inject) => inject(AbsencesMapper),
  ],
  (absences, format) => format.processEmployeeGroups(absences),
);

export const selectEmployeeAbsencesColleaguesStatus = (state: RootState) =>
  state.absences.absenceColleagues.status;

export const selectAdditionalAbsencesColleaguesStatus = (state: RootState) =>
  state.absences.absenceColleagues.additionalStatus;

export const selectAbsencesColleaguesTotal = (state: RootState) =>
  state.absences?.absenceColleagues.total || 0;

export const selectCompanyAbsenceList = createSelector(
  [
    (state: RootState) => state.absences.absenceList.items,
    (_: RootState, inject: Inject) => inject(AbsencesMapper),
  ],
  (absences, format) => format.processCompanyGroups(absences),
);

export const selectEmployeeClosestTask = createSelector(
  [
    (state: RootState) => state.absences.absenceList.items,
    (_: RootState, inject: Inject) => inject(AbsencesMapper),
  ],
  (absences, format) => format.processAbsenceClosestTask(absences),
);

export const selectCompaniesStatus = (state: RootState) =>
  state.absences.companies.status;

export const selectCompanies = (state: RootState) =>
  state.absences.companies.items;

export const selectAbsenceCompanies = (state: RootState) =>
  state.absences.absenceCompanies.items;

export const selectAbsenceCompanyById = createSelector(
  [
    (state: RootState) => state.absences.absenceCompanies.items,
    (state: RootState, inject: Inject, id: string) => id,
  ],
  (companies, id) => companies.find(({ company_id }) => company_id === id),
);

export const selectAbsenceCompaniesStatus = (state: RootState) =>
  state.absences.absenceCompanies.status;

export const selectEmployeeDeadline = (state: RootState) =>
  state.absences.deadline;

export const selectEmployeeCanCreateEvent = (state: RootState) =>
  state.absences.canCreateEvent;

export const selectEmployeeAbsencesGroups = (state: RootState) =>
  state.absences.absenceList.items;

export const selectEmployeeAbsencesColleaguesGroups = (state: RootState) =>
  state.absences.absenceColleagues.items;

export const selectEmployeeEventCreationError = (state: RootState) =>
  state.absences.eventCreationError;

export const selectEmployeeAbsenceTypeItem = createSelector(
  [
    (state: RootState) => state.absences.absenceList.items,
    (_: RootState, inject: Inject) => inject(AbsencesMapper),
  ],
  (absences, format) =>
    format
      .processEmployeeGroups(absences)
      .filter((item) => item.type === 'item'),
);

export const selectCompanyAbsencesStatus = (state: RootState) =>
  state.absences.absenceList.status;

export const selectCompanyTotal = (state: RootState) =>
  state.absences?.absenceList.total || 0;

export const selectAdditionalAbsencesStatus = (state: RootState) =>
  state.absences.absenceList.additionalStatus;

export const selectCompanyAbsencesFilters = createSelector(
  [
    (state: RootState) => state.absences.absenceList.filters,
    (state: RootState, inject: Inject, companyId: string) => companyId,
    (_: RootState, inject: Inject) => inject(AbsencesListMapper),
  ],
  (filters, companyId, format) =>
    filters && format.processListFilters(filters[companyId]),
);

export const selectCompanyAbsencesColleaguesFilters = createSelector(
  [
    (state: RootState) => state.absences.absenceColleagues.filters,
    (state: RootState, inject: Inject, companyId: string) => companyId,
    (_: RootState, inject: Inject) => inject(AbsencesListMapper),
  ],
  (filters, companyId, format) =>
    filters && format.processListFilters(filters[companyId]),
);
export const selectVacationAvailableDays = (state: RootState) =>
  state.absences.availableDays.data.available_days_by_types;

export const selectVacationUnits = createSelector(
  [
    (state: RootState) => state.absences.availableDays.data.units,
    (_: RootState, inject: Inject) => inject(AbsencesListMapper),
  ],
  (units, format) => format.processUnitsList(units),
);

export const selectUnitName = createSelector(
  [
    (state: RootState) => state.absences.availableDays.data.units,
    (_: RootState, inject: Inject) => inject(AbsencesListMapper),
  ],
  (units, format) => format.processUnitsList(units).at(-1)?.name || '',
);

export const selectVacationAvailableDaysStatus = (state: RootState) =>
  state.absences.availableDays.status;
